import { Menu, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { CONTACT_EMAIL, REGISTERED_ADDRESS } from '../content/site';
import { ConsentBanner, ConsentSettingsLink } from './consent-banner';
import { ButtonLink, Container } from './ui';

const NAV = [
  { href: '/#how-it-works', label: 'How it works' },
  { href: '/#what-we-check', label: 'What we check' },
  { href: '/#pricing', label: 'Pricing' },
  { href: '/#about', label: 'About' },
  { href: '/#faq', label: 'FAQ' },
];

export function Wordmark() {
  return (
    <Link
      aria-label="Floto Accessibility Services — home"
      className="flex items-center gap-3 rounded-button"
      to="/"
    >
      <img
        alt=""
        className="size-8"
        height={32}
        src="/icons/icon-128.png"
        width={32}
      />
      <span className="flex flex-col leading-none">
        <span className="font-report text-heading-sm font-semibold text-text-primary">
          Floto
        </span>
        <span className="mt-1 whitespace-nowrap text-small-label uppercase text-text-secondary">
          Accessibility Services
        </span>
      </span>
    </Link>
  );
}

function SiteHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // A route or hash change means a link in the menu was followed.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <header className="sticky top-0 z-30 border-b border-hairline bg-warm/90 backdrop-blur">
      <Container className="flex h-18 items-center justify-between gap-6">
        <Wordmark />
        <nav aria-label="Primary" className="hidden items-center gap-1 md:flex">
          {NAV.map((item) => (
            <a
              className="rounded-button px-3 py-2 text-ui-strong font-medium text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
              href={item.href}
              key={item.href}
            >
              {item.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {/* A wrapper carries `hidden`: on the link itself it would fight the
              button's own `inline-flex`. */}
          <div className="hidden sm:block">
            <ButtonLink size="sm" to="/request">
              Request an audit
            </ButtonLink>
          </div>
          <button
            aria-controls="mobile-nav"
            aria-expanded={open}
            aria-label={open ? 'Close menu' : 'Open menu'}
            className="inline-flex size-11 items-center justify-center rounded-button border border-hairline-alt bg-surface text-text-primary md:hidden"
            onClick={() => setOpen((v) => !v)}
            type="button"
          >
            {open ? (
              <X aria-hidden size={20} />
            ) : (
              <Menu aria-hidden size={20} />
            )}
          </button>
        </div>
      </Container>
      {open ? (
        <nav
          aria-label="Primary"
          className="border-t border-hairline bg-surface md:hidden"
          id="mobile-nav"
        >
          <Container className="flex flex-col py-3">
            {NAV.map((item) => (
              <a
                className="rounded-button px-2 py-3 text-ui-lg font-medium text-text-primary hover:bg-surface-hover"
                href={item.href}
                key={item.href}
              >
                {item.label}
              </a>
            ))}
            <ButtonLink className="mt-2" to="/request">
              Request an audit
            </ButtonLink>
          </Container>
        </nav>
      ) : null}
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-hairline bg-surface">
      <Container className="grid gap-10 py-12 md:grid-cols-4">
        <div className="md:col-span-2">
          <Wordmark />
          <p className="mt-4 max-w-sm text-ui-lg text-text-secondary">
            Expert accessibility audits of websites and web apps against WCAG
            and EN 301 549 — automated depth, verified by specialists.
          </p>
        </div>
        <nav aria-label="Footer">
          <h2 className="text-small-label uppercase text-text-secondary">
            Service
          </h2>
          <ul className="mt-3 space-y-2 text-ui-lg">
            {NAV.map((item) => (
              <li key={item.href}>
                <a
                  className="text-text-primary hover:underline"
                  href={item.href}
                >
                  {item.label}
                </a>
              </li>
            ))}
            <li>
              <Link className="text-text-primary hover:underline" to="/request">
                Request an audit
              </Link>
            </li>
          </ul>
        </nav>
        <div>
          <h2 className="text-small-label uppercase text-text-secondary">
            Contact
          </h2>
          <ul className="mt-3 space-y-2 text-ui-lg">
            <li>
              <a
                className="text-text-primary hover:underline"
                href={`mailto:${CONTACT_EMAIL}`}
              >
                {CONTACT_EMAIL}
              </a>
            </li>
            <li>
              <a
                className="text-text-primary hover:underline"
                href="https://floto.ai"
              >
                floto.ai
              </a>
            </li>
          </ul>
          <address className="mt-4 text-ui not-italic text-text-secondary">
            {REGISTERED_ADDRESS.map((line) => (
              <span className="block" key={line}>
                {line}
              </span>
            ))}
          </address>
        </div>
      </Container>
      <Container>
        <div className="flex flex-col gap-2 border-t border-hairline py-6 text-caption text-text-secondary sm:flex-row sm:justify-between">
          <p>
            &copy; {new Date().getFullYear()} Floto. All rights reserved.
            <ConsentSettingsLink className="ml-4 underline hover:text-text-primary" />
          </p>
          <p>
            An audit is an expert assessment at a point in time, not a legal
            certification of compliance.
          </p>
        </div>
      </Container>
    </footer>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-svh flex-col">
      <a
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-button focus:bg-ink focus:px-4 focus:py-3 focus:text-ui-lg focus:text-white"
        href="#main"
      >
        Skip to content
      </a>
      <ConsentBanner />
      <SiteHeader />
      <main className="flex-1" id="main" tabIndex={-1}>
        {children}
      </main>
      <SiteFooter />
    </div>
  );
}
